/**
 * English strings for the `library` namespace. The source of truth: French is
 * typed against this file. See `apps/web/src/i18n/README.md`.
 */
const library = {
  title: "Library",
  intro:
    "Every video in the workspace — meetings, uploads and published edits. Drag onto a folder to organise; open Studio to cut and combine.",
  sort: {
    label: "Sort",
    newest: "Newest first",
    oldest: "Oldest first",
    longest: "Longest first",
    largest: "Largest first",
    name: "By title",
  },
  folders: {
    heading: "Folders",
    new: "New folder",
    namePlaceholder: "Folder name",
    home: "Home",
    path: "Folder path",
    moveRejected: "That move was rejected.",
    expand: "Expand",
    collapse: "Collapse",
    renameNamed: "Rename {{name}}",
    deleteNamed: "Delete {{name}}",
    confirmDelete:
      "Delete \"{{name}}\"? Recordings and folders inside it move to the top level — nothing is deleted with it.",
    openOrDrop: "Open {{name}} — or drop a video here to file it",
    moveTo: "Move to folder",
  },
  toolbar: {
    search: "Search…",
    searchAll: "Search all videos…",
    searchLibrary: "Search the library…",
    gridView: "Grid view",
    listView: "List view",
    flatHint: "Show every video in one flat list, ignoring folders",
    allVideos: "All videos",
    recordMeeting: "Record a meeting",
    clearSearch: "Clear search",
  },
  count: {
    videos_one: "{{count}} video",
    videos_other: "{{count}} videos",
    folders_one: "{{count}} folder",
    folders_other: "{{count}} folders",
    searching_one: "Searching all folders — {{count}} video matching",
    searching_other: "Searching all folders — {{count}} videos matching",
    workspace_one: "{{count}} video in the whole workspace",
    workspace_other: "{{count}} videos in the whole workspace",
  },
  pagination: {
    previous: "Previous",
    next: "Next",
    pageOf: "Page {{page}} of {{total}}",
  },
  empty: {
    loadRecordingsFailed: "Couldn't load your recordings",
    loadLibraryFailed: "Couldn't load the library",
    reloadHint: "Reload the page, and check your connection if it keeps happening.",
    nothingMatches: "Nothing matches",
    tryDifferent: "Try different words, or clear the search.",
    folderEmpty: "This folder is empty",
    folderEmptyHint: "Drag a recording here, or add one straight to this folder.",
    noRecordings: "No recordings yet",
    noRecordingsHint:
      "Send a notetaker into a Zoom or Google Meet call, upload a file you already have, or import one from YouTube or LinkedIn.",
    noVideos: "No videos yet",
    noVideosHint: "Upload a video, or record a meeting — everything lands here.",
    clearOrPick: "Clear the search or pick another folder.",
  },
  card: {
    stop: "Stop",
    moveNamed: "Move {{title}}",
    dragHint: "Drag to a folder, or press to choose one",
    deleteNamed: "Delete {{title}}",
    deleteRecording: "Delete recording",
    confirmDeleteShort:
      "Delete <strong>\"{{title}}\"</strong>? The video, transcript, chapters and chats go with it. Permanent.",
    confirmDeleteTitle: "Delete \"{{title}}\"?",
    confirmDeleteBody:
      "The video, its transcript, chapters, and every chat about it are removed permanently. This cannot be undone.",
    deletePermanently: "Delete permanently",
    inFolder: "In {{name}}",
  },
  menu: {
    actions: "Actions",
    editInStudio: "Edit in Studio",
    availableWhenReady: "Available once the video is ready.",
    moveToFolder: "Move to folder…",
    rename: "Rename…",
    delete: "Delete…",
    moveTo: "Move to",
    noFolder: "No folder",
    confirmDelete: "Delete this video, its transcript, chapters and chats? This cannot be undone.",
    deleted: "Deleted.",
  },
  deleteImpact: {
    drafts_one: "Used in {{count}} Studio draft — those clips will stop working.",
    drafts_other: "Used in {{count}} Studio drafts — those clips will stop working.",
    edits_one:
      "{{count}} published edit made from it keep playing (they have their own copies), but lose their link to this source.",
    edits_other:
      "{{count}} published edits made from it keep playing (they have their own copies), but lose their link to this source.",
  },
  picker: {
    title: "Add from library",
    hint: "Each pick lands at the end of the timeline — add several, then close and arrange them.",
    folderFilter: "Only show one folder",
    allFolders: "All folders",
    notInFolder: "Not in a folder",
    loadFailedHint: "Close this and try again in a moment.",
    emptyHint: "Upload from the Sources panel, or record a meeting first.",
    alreadyAdded: "Already on the timeline — add it again for a duplicate",
    addToEnd: "Add to the end of the timeline",
    added: "Added",
  },
  upload: {
    limits: "MP4, MOV, WebM or audio · up to {{size}} · up to 10 hours",
    resuming: "Resuming {{percent}}%",
    uploading: "Uploading {{percent}}%",
    tooLarge:
      "That file is {{size}}. The limit is {{limit}}, because larger recordings cannot be transcribed.",
    tooLong: "Recordings must be under 10 hours — longer ones cannot be transcribed.",
    failedResume: "{{message}} Choose the same file again to resume.",
    failed: "The upload failed. Choose the same file again to resume.",
    errors: {
      partRejected: "Part rejected ({{status}})",
      noEtag:
        "The storage bucket is not exposing the ETag header, so the upload cannot be finished.",
      connectionDropped: "The connection dropped.",
      partNotSigned: "A part was not signed.",
    },
  },
  importLink: {
    title: "Import from a link",
    intro: "Paste a {{sources}} link. We fetch the video, then transcribe it like any upload.",
    videoLink: "Video link",
    recognised: "{{source}} link recognised",
    unrecognised: "Paste the link to a {{sources}} video or post itself.",
    titleLabel: "Title",
    titleHint: "(optional — the video’s own title otherwise)",
    lookingOn: "Looking for the video on {{source}}…",
    lookingOnPage: "Looking for the video on the page…",
    rejected: "That link could not be imported.",
    startFailed: "The import could not be started.",
    pasteFirst: "Paste a link first",
    onlySources: "Only {{sources}} links can be imported",
    checking: "Checking the link…",
    submit: "Import video",
    legal:
      "Public and unlisted videos only, up to {{size}} and 10 hours. Import only videos you have the right to use.",
    found:
      "Found “{{title}}”. It is downloading now and appears in your library when ready.",
    stillChecking:
      "Still checking that link. It is in your library, and its card will say if anything is wrong.",
    buttonTitle: "Import a video from {{sources}}",
    buttonCompact: "Link",
    button: "Import link",
  },
  sendBot: {
    title: "Record a meeting",
    intro: "A notetaker joins the call and appears in the participant list.",
    meetingLink: "Meeting link",
    urlPlaceholder: "https://zoom.us/j/… or https://meet.google.com/…",
    recognised: "{{platform}} link recognised",
    unsupported: "Only Zoom and Google Meet links are supported.",
    titleLabel: "Title",
    optional: "(optional)",
    titlePlaceholder: "Weekly sync",
    when: "When",
    joinNow: "Join now",
    joinNowHint: "The call is already running",
    schedule: "Schedule",
    scheduleHint: "For a later meeting",
    tooSoon: "Schedule at least 10 minutes ahead — the bot may miss the start otherwise.",
    joinsEarly: "The bot joins a couple of minutes early and waits.",
    failed: "Could not send the bot.",
    submitNow: "Send the notetaker",
    submitScheduled: "Schedule it",
    consent:
      "Everyone in the call sees the notetaker in the participant list. Recording without telling people is not something this does.",
  },
} as const;

export default library;
